import { HomeGreeting } from '@/components/screens/HomePage/HomeGreetingProps';
import {
  COMMUNITY_CARD_BACKGROUND,
  HOME_POST_RNR_BACKGROUND,
  REFERAL_CARD_BACKGROUND,
} from '@/constants/images';
import { CommunityCard } from '@screens/HomePage/ResultReady/CommunityCard';
import { HealthResultCard } from '@screens/HomePage/ResultReady/HealthResultCard';
import { HealthSummaryCards } from '@screens/HomePage/ResultReady/HealthSummaryCards';
import { HomePageFormulaSection } from '@screens/HomePage/ResultReady/HomePageFormulaSection';
import { MyNutritionCard } from '@screens/HomePage/ResultReady/MyNutritionCard';
import { NutritionEssentials } from '@screens/HomePage/ResultReady/NutritionEssentials';
import { NutritionJourneyDeeperSection } from '@screens/NutritionPage/NutritionJourneyDeeperSection';

import { Render } from '@/components/Helpers/ComponentRender/Render';
import { NotificationCenter } from '@/components/layouts/Notification/NotificationCenter';
import {
  getTimeBasedGreetingAndBgImg,
  isRecentlyPublished,
} from '@/components/utils/Timezone';
import { useBootup } from '@/hooks/useBootup';
import { useUserDetails } from '@/hooks/useUserDetails';
import { JourneyEducationTypes } from '@/utils/JourneyEducationTypes';
import { getBackgroundImageUrlHome } from '@/utils/nutrition/getImageUrl';
import { HomeScoresSection } from '@screens/HomePage/ResultReady/HomeScoresSection';
import { HomeStorifyComponent } from '@screens/HomePage/ResultReady/HomeStorifyComponent';
import EducationVideoCarousel from '../EducationVideoCarousel/EducationVideoCarousel';
import { HomeActivateJourney } from '../HomeActivateJourney';
import { HomeTrackProgress } from '../HomeTrackProgress';
import { FeatureFoodCard } from './FeatureFoodCard';
import { useRef } from 'react';
import { useScrollToElementOnLoad } from '@/hooks/useScrollToElementOnLoad';

export const ResultsReady = () => {
  const { bootupState } = useBootup();
  const { firstName } = useUserDetails();
  const communitySectionRef = useRef<HTMLDivElement>(null);
  const isResultsReleasedRecently = isRecentlyPublished(
    bootupState?.rnrLastGeneratedDate,
  );
  const isReTestProcessing = bootupState?.kitDetailsData?.kitReIssued;

  function getHeroSectionBgImg() {
    if (isResultsReleasedRecently) {
      return HOME_POST_RNR_BACKGROUND;
    } else {
      const timeBasedGreeting = getTimeBasedGreetingAndBgImg();
      return isReTestProcessing
        ? timeBasedGreeting.retestBgImg
        : timeBasedGreeting.bgImg;
    }
  }

  const NAVBAR_OFFSET = 70;

  useScrollToElementOnLoad({
    targetId: 'community',
    targetRef: communitySectionRef,
    loadingStates: [],
    scrollOffset: NAVBAR_OFFSET,
    isEnabled: !!bootupState,
  });

  return (
    <>
      {/* Home screen - Hero section */}
      <div
        className="relative"
        style={{
          backgroundImage: `url(${getHeroSectionBgImg()})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="page-container">
          <div className="flex justify-between">
            <Render when={firstName}>
              <HomeGreeting userName={firstName} className="text-white" />
            </Render>
            <div className="mr-6 md:mr-0">
              <NotificationCenter />
            </div>
          </div>
          {isResultsReleasedRecently ? (
            <div className="pt-16">
              <HealthResultCard />
            </div>
          ) : (
            <>
              <div className="h-40" />
              <HomeStorifyComponent isResultsReadyRecently={false} />
            </>
          )}
          {/* Home screen - FeaturedFoodCondition */}
          {!isResultsReleasedRecently &&
            !isReTestProcessing &&
            bootupState?.kitDetailsData?.kitType !== 'OHI' && (
              <div className="pt-12">
                <FeatureFoodCard />
              </div>
            )}
        </div>
      </div>

      <Render when={isResultsReleasedRecently}>
        <div className="bg-[#E4ECF4]">
          <div className="page-container !pb-0">
            <HomeStorifyComponent isResultsReadyRecently={isResultsReleasedRecently} />
          </div>
        </div>
      </Render>

      {/* Home screen - Retest users result tracking */}
      <Render when={isReTestProcessing}>
        <div className="bg-[#E4ECF4]">
          <div className="page-container">
            <HomeActivateJourney screen="nornr" screenType="retest" />
            <div className="pt-8">
              <HomeTrackProgress screen="nornr" />
            </div>
          </div>
        </div>
      </Render>

      {/* Home screen - HealthSummary horizntal flow */}
      <div className="relative transition-all duration-500 bg-[#E4ECF4]">
        <div className="page-container grid grid-cols-1 !py-12">
          <HealthSummaryCards />
        </div>
      </div>
      <div
        className="relative transition-all duration-500"
        style={{
          background: '#E4ECF4',
        }}
      >
        <div className="page-container !pt-0">
          <HomeScoresSection />
        </div>
      </div>

      <div
        className="relative transition-all duration-500"
        style={{
          background: '#E4ECF4',
        }}
      >
        <div className="page-container bg-[#E4ECF4] grid grid-cols-1 !py-12">
          <NutritionEssentials />
        </div>
      </div>

      <div
        className="relative transition-all duration-500"
        style={{
          background: '#E4ECF4',
        }}
      >
        <div className="page-container bg-[#E4ECF4] ">
          <MyNutritionCard />
        </div>
      </div>

      <section
        className="grid grid-cols-1 !py-6 bg-cover bg-no-repeat"
        style={{
          backgroundImage: `url(${getBackgroundImageUrlHome('formula_bg_large.png')})`,
        }}
      >
        <HomePageFormulaSection />
      </section>

      <div className="grid grid-cols-1 bg-[#E4ECF4] -mt-8">
        <div className="page-container">
          <EducationVideoCarousel screen="home" />
        </div>
      </div>

      <div ref={communitySectionRef}>
        <section
          className="relative transition-all duration-500"
          style={{
            background: '#E4ECF4',
          }}
        >
          <div className="page-container">
            <div className="flex flex-col lg:flex-row justify-center gap-2">
              <CommunityCard
                backgroundImage={COMMUNITY_CARD_BACKGROUND}
                title="Join the Community"
                description="Join the conversation and connect with fellow wellness enthusiasts on a journey to improve their health."
                buttonText="Join now"
                externalLink="https://viome.mn.co/"
                eventName="home_join_the_community"
              />
              <CommunityCard
                backgroundImage={REFERAL_CARD_BACKGROUND}
                title="Give $50, Get $50"
                description="When you refer a friend to join the Viome community, you'll both benefit."
                buttonText="Refer a friend"
                externalLink="https://buy.viome.com/pages/referral"
                eventName="home_refer_a_friend"
              />
            </div>
            <div className="pb-20">
              {bootupState != null && (
                <NutritionJourneyDeeperSection
                  sectionInfo={
                    isReTestProcessing
                      ? JourneyEducationTypes.HOME_POST_RNR_RETEST
                      : JourneyEducationTypes.HOME_POST_RNR
                  }
                  screen="home"
                />
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
